import generateAgentGuidance from '../generateAgentGuidance';
import { fetchChatCompletion } from '../api';
import { API_CONFIG } from '../../config/constants';

// Keep track of query IDs that have already been shown to the user
const shownQueryIds = new Set();

// Cache for query relevance decisions to avoid repeated API calls
const relevanceCache = new Map();

/**
 * Determine if a query is relevant to the current topic using agent prompt
 * @param {string} queryId - The ID of the query
 * @param {Object} queries - All available queries
 * @param {string} topic - The current topic being discussed
 * @returns {Promise<boolean>} Whether the query is relevant to the topic
 */
const isQueryRelevantToTopic = async (queryId, queries, topic) => {
  const query = queries[queryId];
  if (!query || !query.name) {
    return false;
  }
  
  // Check cache first to avoid repeated API calls
  const cacheKey = `${queryId}:${topic}`;
  if (relevanceCache.has(cacheKey)) {
    return relevanceCache.get(cacheKey);
  }
  
  // For default topic, all queries are relevant
  if (!topic || topic === 'default') {
    relevanceCache.set(cacheKey, true);
    return true;
  }
  
  // For efficiency, use some basic heuristics before calling the API
  // If the query name or category directly mentions the topic, it's relevant
  const queryName = query.name.toLowerCase();
  const queryDescription = (query.description || '').toLowerCase();
  const queryCategory = (query.category || '').toLowerCase();
  
  if (queryName.includes(topic) || queryDescription.includes(topic) || queryCategory.includes(topic)) {
    console.log(`Query "${queryName}" is directly relevant to topic "${topic}" (heuristic match)`);
    relevanceCache.set(cacheKey, true);
    return true;
  }
  
  // Prepare a prompt for the agent to determine relevance
  const prompt = `
    You are a recruitment assistant helping to determine if a search filter is relevant to a specific topic.
    
    Topic: ${topic}
    
    Filter details:
    - Name: ${query.name}
    - Description: ${query.description || 'No description provided'}
    - Category: ${query.category || 'No category provided'}
    
    Is this filter relevant to the "${topic}" topic in a recruitment conversation?
    Consider the semantics and context, not just keyword matching.
    
    IMPORTANT: Return ONLY the word "yes" or "no" without any explanation, quotes, or formatting.
  `;
  
  try {
    // Use a lightweight model for this determination to minimize latency
    const response = await fetchChatCompletion(
      API_CONFIG.url,
      prompt,
      "Determine filter relevance"
    );
    
    // Parse the response - it could be a string "yes"/"no" or an object with a response property
    const isRelevant = response && 
                      (typeof response === 'string' ? 
                        response.toLowerCase().includes('yes') : 
                        (typeof response === 'object' && response.response ? 
                          response.response.toLowerCase().includes('yes') : 
                          false));
    
    // Log the result
    console.log(`Query "${queryName}" relevance to "${topic}": ${isRelevant ? 'Relevant' : 'Not relevant'} (agent decision)`);
    
    // Cache the result
    relevanceCache.set(cacheKey, isRelevant);
    
    return isRelevant;
  } catch (error) {
    console.error('Error determining query relevance:', error);
    
    // Fallback to keyword-based matching if AI-based approach fails
    console.log('Falling back to keyword-based matching for query relevance');
    
    // Define topic-specific keywords for fallback
    const topicKeywords = {
      skills: ['skill', 'technology', 'language', 'framework', 'tool', 'proficiency', 'expertise', 'code', 'programming', 'development', 'software'],
      experience: ['experience', 'year', 'senior', 'junior', 'mid-level', 'career', 'history', 'background', 'tenure', 'work'],
      location: ['location', 'remote', 'onsite', 'hybrid', 'city', 'country', 'region', 'relocation', 'commute', 'timezone'],
      title: ['title', 'role', 'position', 'job', 'level', 'seniority', 'rank', 'designation'],
      company: ['company', 'industry', 'sector', 'organization', 'enterprise', 'startup', 'business', 'employer', 'corporate'],
      education: ['education', 'degree', 'university', 'college', 'school', 'certification', 'diploma', 'academic', 'graduate', 'bachelor', 'master', 'phd']
    };
    
    // Default keywords for any topic
    const defaultKeywords = ['candidate', 'search', 'filter', 'match', 'requirement'];
    
    // Get keywords for the current topic
    const keywords = topicKeywords[topic] || defaultKeywords;
    
    // Check if query name or description contains any of the topic keywords
    const matchingKeywords = keywords.filter(keyword => 
      queryName.includes(keyword) || queryDescription.includes(keyword) || (queryCategory && queryCategory.includes(keyword))
    );
    
    const isRelevantByKeyword = matchingKeywords.length > 0;
    
    // Log the fallback result
    console.log(`Query "${queryName}" relevance to "${topic}": ${isRelevantByKeyword ? 'Relevant' : 'Not relevant'} (keyword fallback)`);
    if (isRelevantByKeyword) {
      console.log(`Matching keywords: ${matchingKeywords.join(', ')}`);
    }
    
    // Cache the result
    relevanceCache.set(cacheKey, isRelevantByKeyword);
    
    return isRelevantByKeyword;
  }
};

/**
 * Filter queries to only include those relevant to the current topic and not shown before
 * @param {Array} queryIds - Array of query IDs
 * @param {Object} queries - All available queries
 * @param {string} topic - The current topic being discussed
 * @returns {Promise<Array>} Filtered array of query IDs
 */
const filterRelevantQueries = async (queryIds, queries, topic) => {
  // If no specific topic, return all unshown queries
  if (!topic || topic === 'default') {
    const unshownQueries = queryIds.filter(id => !shownQueryIds.has(id));
    console.log(`Default topic - Original queries: ${queryIds.length}, Unshown queries: ${unshownQueries.length}`);
    return unshownQueries;
  }
  
  // First filter out queries that have already been shown
  const unshownQueries = queryIds.filter(id => !shownQueryIds.has(id));
  console.log(`Topic: ${topic} - Original queries: ${queryIds.length}, Unshown queries: ${unshownQueries.length}`);
  
  // Then filter based on relevance to topic (using Promise.all for parallel processing)
  const relevancePromises = unshownQueries.map(async id => {
    const isRelevant = await isQueryRelevantToTopic(id, queries, topic);
    return { id, isRelevant };
  });
  
  const relevanceResults = await Promise.all(relevancePromises);
  const filtered = relevanceResults
    .filter(result => result.isRelevant)
    .map(result => result.id);
  
  // Log filtering results for debugging
  console.log(`Topic: ${topic} - Unshown queries: ${unshownQueries.length}, Relevant queries: ${filtered.length}`);
  if (filtered.length > 0) {
    console.log('Relevant queries for', topic, ':', filtered.map(id => queries[id]?.name || id).join(', '));
  }
  
  return filtered;
};

/**
 * Generate enhanced agent guidance with detailed Findem Magic explanations
 * @param {Object} aiMatchesResult - Result from runAIMatches
 * @param {Object} decision - Current decision object
 * @param {string} topic - The current topic being discussed
 * @param {Array} topSkills - Optional array of top skills from skills_map
 * @returns {Promise<string>} Enhanced agent guidance with Findem Magic explanations
 */
export const generateEnhancedGuidance = async (aiMatchesResult, decision, topic, topSkills = []) => {
  // Default to standard guidance
  let enhancedAgentGuidance = null;
  
  // Add top skills information if available and topic is skills
  if (topic === 'skills' && topSkills && topSkills.length > 0) {
    let topSkillsMessage = "Based on my analysis of candidate profiles, these are the most common skills for this role:\n\n";
    
    // Add each top skill with its count
    topSkills.forEach(({ skill, count }) => {
      topSkillsMessage += `• **${skill}**: Found in ${count} profiles\n`;
    });
    
    topSkillsMessage += "\nWe have identified that your employees also possess these top skills. We are adding these to help you understand which skills are most prevalent among your employees and candidates. Would you like to focus on any of these skills in particular?";
    
    // Set as enhanced guidance
    enhancedAgentGuidance = `Agent guidance: ${topSkillsMessage}\n\nPlease acknowledge the user's requirements and suggest how these common skills align with their needs.`;
    
    // Log the enhanced guidance for skills
    console.log('ENHANCED GUIDANCE - SKILLS TOPIC WITH TOP SKILLS:', {
      topic,
      topSkills: topSkills.map(s => `${s.skill} (${s.count})`),
      guidancePreview: enhancedAgentGuidance.substring(0, 200) + '...'
    });
  }
  
  // Check if we have Findem Magic recommendations and queries
  if (aiMatchesResult.search_gen_response && 
      aiMatchesResult.search_gen_response.icp_requirements &&
      decision.queries) {
    
    const icpRequirements = aiMatchesResult.search_gen_response.icp_requirements;
    const findemMagic = icpRequirements.find(req => req.section_name === "Findem Magic");
    
    if (findemMagic && findemMagic.bool_str) {
      // Extract query IDs from the bool_str
      const queryIds = findemMagic.bool_str.match(/[0-9a-f]{24}/g) || [];
      
      if (queryIds.length > 0 && Object.keys(decision.queries || {}).length > 0) {
        // Filter query IDs based on topic relevance
        const relevantQueryIds = await filterRelevantQueries(queryIds, decision.queries, topic);
        
        if (!relevantQueryIds || relevantQueryIds.length === 0) {
          // No relevant queries for this topic, use standard guidance
          return null;
        }
        
        // Create a personalized message about the Findem Magic suggestions
        let magicSuggestions = "";
        
        // Customize the intro based on the topic
        switch (topic) {
          case 'skills':
            magicSuggestions = "I've analyzed your skills requirements and have some additional suggestions that might help refine your search:\n\n";
            break;
          case 'experience':
            magicSuggestions = "Based on the experience requirements you've shared, I've identified some specialized filters that could enhance your search:\n\n";
            break;
          case 'location':
            magicSuggestions = "Looking at your location preferences, I've found some additional filters that might be useful:\n\n";
            break;
          case 'title':
            magicSuggestions = "Based on the job title you're looking for, I've identified some specialized filters that could help:\n\n";
            break;
          case 'company':
            magicSuggestions = "Considering your company and industry preferences, these additional filters might be valuable:\n\n";
            break;
          case 'education':
            magicSuggestions = "Based on your education requirements, I've identified these specialized filters:\n\n";
            break;
          default:
            magicSuggestions = "Based on our conversation, I've identified some additional filters that might enhance your search:\n\n";
        }
        
        // Add detailed explanations for each relevant query and mark as shown
        relevantQueryIds.forEach(id => {
          if (decision.queries[id]) {
            const queryName = decision.queries[id].name || "Additional filter";
            const queryDescription = decision.queries[id].description || "Enhances your search with relevant candidates";
            
            magicSuggestions += `• **${queryName}**: ${queryDescription}\n`;
            
            // Mark this query as shown so it won't be repeated in other topics
            shownQueryIds.add(id);
          }
        });
        
        // Log which queries have been shown so far
        console.log('Queries shown so far:', Array.from(shownQueryIds).length);
        
        // Customize the conclusion based on the topic
        switch (topic) {
          case 'skills':
            magicSuggestions += "\nThese additional filters will help you find candidates who match your skills requirements more precisely. Would you like me to include these in your search?";
            break;
          case 'experience':
            magicSuggestions += "\nThese specialized filters can help you find candidates with the right experience profile. Would you like to include them?";
            break;
          default:
            magicSuggestions += "\nThese specialized filters can help you find more relevant candidates. Would you like to include these in your search?";
        }
        
        // Create enhanced guidance
        enhancedAgentGuidance = `Agent guidance: ${magicSuggestions}\n\nPlease acknowledge the user's requirements and confirm if these additional suggestions align with their needs.`;
      }
    }
  }
  
  // Check if we already have enhanced guidance from top skills
  if (enhancedAgentGuidance) {
    return enhancedAgentGuidance;
  }
  
  // Check if we have Findem Magic recommendations and queries
  if (aiMatchesResult.search_gen_response && 
      aiMatchesResult.search_gen_response.icp_requirements &&
      decision.queries) {
    
    const icpRequirements = aiMatchesResult.search_gen_response.icp_requirements;
    const findemMagic = icpRequirements.find(req => req.section_name === "Findem Magic");
    
    if (findemMagic && findemMagic.bool_str) {
      // Extract query IDs from the bool_str
      const queryIds = findemMagic.bool_str.match(/[0-9a-f]{24}/g) || [];
      
      if (queryIds.length > 0 && Object.keys(decision.queries || {}).length > 0) {
        // Filter query IDs based on topic relevance
        const relevantQueryIds = await filterRelevantQueries(queryIds, decision.queries, topic);
        
        if (!relevantQueryIds || relevantQueryIds.length === 0) {
          // No relevant queries for this topic, use standard guidance
          return null;
        }
        
        // Create a personalized message about the Findem Magic suggestions
        let magicSuggestions = "";
        
        // Customize the intro based on the topic
        switch (topic) {
          case 'skills':
            // If we have top skills, include them in the message
            if (topSkills && topSkills.length > 0) {
              magicSuggestions = "I've analyzed your skills requirements and the most common skills in this field. Here are some additional suggestions that might help refine your search:\n\n";
              
              // Add top skills section
              magicSuggestions += "**Most common skills in this field:**\n";
              topSkills.forEach(({ skill, count }) => {
                magicSuggestions += `• **${skill}**: Found in ${count} profiles\n`;
              });
              
              magicSuggestions += "\n**Additional filters to consider:**\n";
            } else {
              magicSuggestions = "I've analyzed your skills requirements and have some additional suggestions that might help refine your search:\n\n";
            }
            break;
          case 'experience':
            magicSuggestions = "Based on the experience requirements you've shared, I've identified some specialized filters that could enhance your search:\n\n";
            break;
          case 'location':
            magicSuggestions = "Looking at your location preferences, I've found some additional filters that might be useful:\n\n";
            break;
          case 'title':
            magicSuggestions = "Based on the job title you're looking for, I've identified some specialized filters that could help:\n\n";
            break;
          case 'company':
            magicSuggestions = "Considering your company and industry preferences, these additional filters might be valuable:\n\n";
            break;
          case 'education':
            magicSuggestions = "Based on your education requirements, I've identified these specialized filters:\n\n";
            break;
          default:
            magicSuggestions = "Based on our conversation, I've identified some additional filters that might enhance your search:\n\n";
        }
        
        // Add detailed explanations for each relevant query and mark as shown
        relevantQueryIds.forEach(id => {
          if (decision.queries[id]) {
            const queryName = decision.queries[id].name || "Additional filter";
            const queryDescription = decision.queries[id].description || "Enhances your search with relevant candidates";
            
            magicSuggestions += `• **${queryName}**: ${queryDescription}\n`;
            
            // Mark this query as shown so it won't be repeated in other topics
            shownQueryIds.add(id);
          }
        });
        
        // Log which queries have been shown so far
        console.log('Queries shown so far:', Array.from(shownQueryIds).length);
        
        // Customize the conclusion based on the topic
        switch (topic) {
          case 'skills':
            magicSuggestions += "\nThese additional filters will help you find candidates who match your skills requirements more precisely. Would you like me to include these in your search?";
            break;
          case 'experience':
            magicSuggestions += "\nThese specialized filters can help you find candidates with the right experience profile. Would you like to include them?";
            break;
          default:
            magicSuggestions += "\nThese specialized filters can help you find more relevant candidates. Would you like to include these in your search?";
        }
        
        // Create enhanced guidance
        enhancedAgentGuidance = `Agent guidance: ${magicSuggestions}\n\nPlease acknowledge the user's requirements and confirm if these additional suggestions align with their needs.`;
      }
    }
  }
  
  // Use enhanced guidance if available, otherwise use standard guidance
  return enhancedAgentGuidance || generateAgentGuidance(
    aiMatchesResult, 
    decision.topicResponse, 
    decision.queries, 
    decision.updatedDiscoveryState
  );
};

export default generateEnhancedGuidance;
